import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";

const TYPES = {
  auto_percent: "Auto -% (tout le site)",
  code_percent: "Code -%",
  code_fixed: "Code -CHF",
  bogo: "BOGO (X achetés = 1 offert)",
};
const empty = { name: "", type: "auto_percent", active: true, value: 10, code: "", bogo_buy: 2, min_order: 0 };

export default function PromotionsTab() {
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/promotions").then((r) => setPromos(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Nom requis");
    const payload = { ...form, value: parseFloat(form.value) || 0, bogo_buy: parseInt(form.bogo_buy) || 2, min_order: parseFloat(form.min_order) || 0 };
    try {
      if (editing === "new") await api.post("/promotions", payload);
      else await api.put(`/promotions/${editing}`, { ...payload, id: editing });
      toast.success("Promotion enregistrée"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (p) => { await api.delete(`/promotions/${p.id}`); toast.success("Supprimée"); load(); };
  const toggle = async (p) => { await api.put(`/promotions/${p.id}`, { ...p, active: !p.active }); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-2xl font-bold">Promotions</h2>
        <Button onClick={() => { setForm(empty); setEditing("new"); }} className="rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="new-promo-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle</Button>
      </div>

      <div className="space-y-2">
        {promos.map((p) => (
          <div key={p.id} className="flex items-center gap-3 border border-white/15 p-3" data-testid={`admin-promo-${p.id}`}>
            <div className="flex-1">
              <p className="font-medium">{p.name} {p.code && <span className="text-brand-amber font-mono text-sm">[{p.code}]</span>}</p>
              <p className="text-xs text-white/50">{TYPES[p.type]} · {p.type === "bogo" ? `acheter ${p.bogo_buy}` : `valeur ${p.value}`} {p.min_order > 0 && `· min ${p.min_order} CHF`}</p>
            </div>
            <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`toggle-promo-${p.id}`} />
            <Button onClick={() => { setForm(p); setEditing(p.id); }} variant="outline" className="rounded-none border-white/30 bg-transparent h-8 w-8 p-0"><Pencil className="w-4 h-4" /></Button>
            <Button onClick={() => del(p)} variant="outline" className="rounded-none border-red-500/40 text-red-400 bg-transparent h-8 w-8 p-0" data-testid={`del-promo-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-none border-2 border-white bg-neutral-900 text-white">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing === "new" ? "Nouvelle promotion" : "Modifier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="promo-name" /></div>
            <div><Label>Type</Label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-none bg-neutral-800 border border-white/20 px-2 py-2 text-sm" data-testid="promo-type">
                {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {form.type !== "bogo" && <div><Label>Valeur ({form.type === "code_fixed" ? "CHF" : "%"})</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" data-testid="promo-value" /></div>}
            {form.type === "bogo" && <div><Label>Nombre à acheter</Label><Input type="number" value={form.bogo_buy} onChange={(e) => setForm({ ...form, bogo_buy: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" /></div>}
            {form.type !== "auto_percent" && <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none bg-neutral-800 border-white/20 font-mono" data-testid="promo-code" /></div>}
            <div><Label>Commande minimum (CHF)</Label><Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="rounded-none bg-neutral-800 border-white/20" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            <Button onClick={save} className="w-full rounded-none bg-brand-red hover:bg-brand-red/90" data-testid="save-promo-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
